import { createContext, ReactNode, useContext, useEffect, useReducer } from 'react'
import { ClassImpactQuestions, CurrentUser } from '~/shared/api-generated-types'

export enum ImpactQuestionsActions {
  SET_PRE_CLASS_QUESTIONS_AVAILABLE,
  SET_PRE_CLASS_QUESTIONS_ASKED,
  SET_PRE_CLASS_QUESTIONS_SKIPPED,
  SET_PRE_CLASS_QUESTIONS_SUBMITTED,
  SET_POST_CLASS_QUESTIONS_AVAILABLE,
  SET_POST_CLASS_QUESTIONS_ASKED,
  SET_POST_CLASS_QUESTIONS_SKIPPED,
  SET_POST_CLASS_QUESTIONS_SUBMITTED,
  RESET,
}

type ImpactQuestionsContext = {
  isPreClassQuestionsAvailable: boolean
  isPreClassQuestionsAsked: boolean
  isPreClassQuestionsSkipped: boolean
  isPreClassQuestionsSubmitted: boolean
  isPostClassQuestionsAvailable: boolean
  isPostClassQuestionsAsked: boolean
  isPostClassQuestionsSkipped: boolean
  isPostClassQuestionsSubmitted: boolean
}

type ImpactQuestionsContextType = {
  state: ImpactQuestionsContext
  dispatch: React.Dispatch<ImpactQuestionsActions>
}

const impactQuestionsDefaultValues: ImpactQuestionsContext = {
  isPreClassQuestionsAvailable: false,
  isPreClassQuestionsAsked: false,
  isPreClassQuestionsSkipped: false,
  isPreClassQuestionsSubmitted: false,
  isPostClassQuestionsAvailable: false,
  isPostClassQuestionsAsked: false,
  isPostClassQuestionsSkipped: false,
  isPostClassQuestionsSubmitted: false,
}

const ImpactQuestionsContext = createContext<ImpactQuestionsContextType>({
  state: impactQuestionsDefaultValues,
  dispatch: () => {},
})

type Props = {
  currentUser: CurrentUser
  classImpactQuestions?: ClassImpactQuestions
  children: ReactNode
}

function reducer(state: ImpactQuestionsContext, action: ImpactQuestionsActions): ImpactQuestionsContext {
  switch (action) {
    case ImpactQuestionsActions.SET_PRE_CLASS_QUESTIONS_AVAILABLE:
      return { ...state, isPreClassQuestionsAvailable: true }

    case ImpactQuestionsActions.SET_PRE_CLASS_QUESTIONS_ASKED:
      return { ...state, isPreClassQuestionsAsked: true }

    case ImpactQuestionsActions.SET_PRE_CLASS_QUESTIONS_SKIPPED:
      return { ...state, isPreClassQuestionsSkipped: true }

    case ImpactQuestionsActions.SET_PRE_CLASS_QUESTIONS_SUBMITTED:
      return { ...state, isPreClassQuestionsSubmitted: true }

    case ImpactQuestionsActions.SET_POST_CLASS_QUESTIONS_AVAILABLE:
      return { ...state, isPostClassQuestionsAvailable: true }

    case ImpactQuestionsActions.SET_POST_CLASS_QUESTIONS_ASKED:
      return { ...state, isPostClassQuestionsAsked: true }

    case ImpactQuestionsActions.SET_POST_CLASS_QUESTIONS_SKIPPED:
      return { ...state, isPostClassQuestionsSkipped: true }

    case ImpactQuestionsActions.SET_POST_CLASS_QUESTIONS_SUBMITTED:
      return { ...state, isPostClassQuestionsSubmitted: true }

    case ImpactQuestionsActions.RESET:
      return {
        ...state,
        isPreClassQuestionsAsked: false,
        isPostClassQuestionsAsked: false,
      }

    default:
      return state
  }
}

export function ImpactQuestionsProvider({ children, currentUser, classImpactQuestions }: Props): JSX.Element {
  const [state, dispatch] = useReducer(reducer, impactQuestionsDefaultValues)

  useEffect(() => {
    if (currentUser.authenticatedUser?.id && classImpactQuestions?.preClassQuestions?.length > 0) {
      dispatch(ImpactQuestionsActions.SET_PRE_CLASS_QUESTIONS_AVAILABLE)
    }

    if (currentUser.authenticatedUser?.id && classImpactQuestions?.postClassQuestions?.length > 0) {
      dispatch(ImpactQuestionsActions.SET_POST_CLASS_QUESTIONS_AVAILABLE)
    }
  }, [currentUser.authenticatedUser?.id, classImpactQuestions])

  return (
    <ImpactQuestionsContext.Provider
      value={{
        state,
        dispatch,
      }}
    >
      {children}
    </ImpactQuestionsContext.Provider>
  )
}

export function useImpactQuestions(): ImpactQuestionsContextType {
  return useContext(ImpactQuestionsContext)
}
